import { expandFixed } from './models/fixed';
import { parseRecurrence } from './parse-recurrence';
import type { PreviousOccurrence, ScheduleConfig, Window } from './types';

/**
 * Compute the next occurrence window at the period after `previousOccurrence`
 * (`null` → period 0). Both series expand independently from their own seed and
 * pair by index.
 *
 * Returns `null` only when a terminating rule is exhausted:
 * - `COUNT=N`: emitted for periods `0..N-1`; period `N` onward → `null`.
 * - `UNTIL=U`: emitted while `due_n <= U` (day-inclusive); the first due past `U` → `null`.
 * - Neither: never terminates.
 *
 * Throws `RangeError` when `previousOccurrence.PeriodIndex` is not a
 * non-negative integer, and via `parseRecurrence` (invalid RRULE) /
 * `expandFixed` (invalid seed, window date out of range).
 */
export const computeNextWindow = (
  config: ScheduleConfig,
  previousOccurrence: PreviousOccurrence | null
): Window | null => {
  if (
    previousOccurrence !== null &&
    (!Number.isInteger(previousOccurrence.PeriodIndex) ||
      previousOccurrence.PeriodIndex < 0)
  ) {
    // A caller-contract violation (bad cursor read, corrupted row), not bad
    // user config — RangeError, mirroring previewWindows's guard on `n`.
    throw new RangeError(
      `computeNextWindow: previousOccurrence.PeriodIndex must be a non-negative integer, got ${previousOccurrence.PeriodIndex}`
    );
  }

  const rule = parseRecurrence(config.RRule);
  const n =
    previousOccurrence === null ? 0 : previousOccurrence.PeriodIndex + 1;

  if (rule.count !== undefined && n >= rule.count) {
    return null;
  }

  const windowStart = expandFixed(config.FirstStartDate, {
    freq: rule.freq,
    interval: rule.interval,
    n,
  });
  const windowDue = expandFixed(config.FirstDueDate, {
    freq: rule.freq,
    interval: rule.interval,
    n,
  });

  // Both are midnight-UTC days, so a due landing on UNTIL compares <= and is emitted.
  if (rule.until !== undefined && windowDue.getTime() > rule.until.getTime()) {
    return null;
  }

  return { WindowStart: windowStart, WindowDue: windowDue };
};
